import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

function fmtMoney(n) { return Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'revenue', 'expense'];
const TYPE_COLORS = { asset: 'badge-processing', liability: 'badge-overdue', equity: 'badge-admin', revenue: 'badge-active', expense: 'badge-pending' };

export default function Accounting({ setTitle }) {
  const [tab, setTab] = useState('accounts');
  useEffect(() => { setTitle('Accounting'); }, [setTitle]);

  return (
    <div className="page">
      <div className="page-header"><h1>Accounting</h1></div>
      <div className="card">
        <div className="tab-bar">
          {[['accounts','Chart of Accounts'], ['journal','Journal Entries'], ['ledger','Ledger'], ['trial','Trial Balance']].map(([key, label]) => (
            <button key={key} className={`tab-btn ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>
        {tab === 'accounts' && <ChartOfAccounts />}
        {tab === 'journal'  && <JournalEntries />}
        {tab === 'ledger'   && <Ledger />}
        {tab === 'trial'    && <TrialBalance />}
      </div>
    </div>
  );
}

/* ─── Chart of Accounts ────────────────────────────────────── */
const ACCOUNT_INIT = { code: '', name: '', type: 'asset', parent_id: '', description: '', is_active: 1 };

function ChartOfAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(ACCOUNT_INIT);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/accounting/accounts?type=${typeFilter}&search=${encodeURIComponent(search)}`);
      setAccounts(data || []);
    } catch { setAccounts([]); }
    finally { setLoading(false); }
  }, [typeFilter, search]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(ACCOUNT_INIT); setEditId(null); setModal(true); };
  const openEdit = (a) => { setForm({ code: a.code, name: a.name, type: a.type, parent_id: a.parent_id || '', description: a.description || '', is_active: a.is_active }); setEditId(a.id); setModal(true); };
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim() || !form.type) return alert('Code, Name and Type are required');
    setSaving(true);
    try {
      if (editId) await api.put(`/accounting/accounts/${editId}`, form);
      else        await api.post('/accounting/accounts', form);
      setModal(false); load();
    } catch (err) { alert(err.response?.data?.error || 'Error saving account'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete account "${name}"?`)) return;
    try { await api.delete(`/accounting/accounts/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Cannot delete'); }
  };

  // Group by type for display
  const grouped = ACCOUNT_TYPES.reduce((acc, t) => {
    acc[t] = accounts.filter(a => a.type === t && (!typeFilter || a.type === typeFilter));
    return acc;
  }, {});

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="search-box" style={{ maxWidth: 260 }}>
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input placeholder="Search accounts…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-control" style={{ width: 150 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={openAdd}>+ Add Account</button>
      </div>

      {loading ? <div className="loading">Loading…</div> : (
        ACCOUNT_TYPES.filter(t => !typeFilter || t === typeFilter).map(t => (
          grouped[t].length === 0 ? null : (
            <div key={t} className="report-section">
              <h4 style={{ textTransform: 'capitalize' }}>{t} Accounts</h4>
              <table>
                <thead><tr><th>Code</th><th>Name</th><th>Type</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {grouped[t].map(a => (
                    <tr key={a.id}>
                      <td className="font-mono"><strong>{a.code}</strong></td>
                      <td>
                        {a.name}
                        {a.description && <div className="text-muted" style={{ fontSize: 11 }}>{a.description}</div>}
                      </td>
                      <td><span className={`badge ${TYPE_COLORS[a.type]}`}>{a.type}</span></td>
                      <td><span className={`badge ${a.is_active ? 'badge-active' : 'badge-inactive'}`}>{a.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <div className="actions">
                          <button className="btn-icon" title="Edit" onClick={() => openEdit(a)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(a.id, a.name)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ))
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId ? 'Edit Account' : 'New Account'}</h2>
              <button className="modal-close" onClick={() => setModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Account Code *</label>
                  <input className="form-control" value={form.code} onChange={e => f('code', e.target.value)} placeholder="e.g. 1010" />
                </div>
                <div className="form-group">
                  <label>Account Type *</label>
                  <select className="form-control" value={form.type} onChange={e => f('type', e.target.value)}>
                    {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Account Name *</label>
                  <input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} placeholder="e.g. Cash on Hand" />
                </div>
                <div className="form-group full">
                  <label>Description</label>
                  <input className="form-control" value={form.description} onChange={e => f('description', e.target.value)} placeholder="Optional description" />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.is_active} onChange={e => f('is_active', Number(e.target.value))}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : editId ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Journal Entries ──────────────────────────────────────── */
const BLANK_LINE = { account_id: '', description: '', debit: '', credit: '' };

function JournalEntries() {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [status, setStatus]   = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm]       = useState({ date: new Date().toISOString().slice(0, 10), description: '', reference: '', status: 'draft', lines: [{ ...BLANK_LINE }, { ...BLANK_LINE }] });
  const [saving, setSaving]   = useState(false);
  const LIMIT = 20;

  useEffect(() => {
    api.get('/accounting/accounts').then(r => setAccounts(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/accounting/journal?status=${status}&page=${page}&limit=${LIMIT}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [status, page]);

  useEffect(() => { load(); }, [load]);

  const addLine = () => setForm(p => ({ ...p, lines: [...p.lines, { ...BLANK_LINE }] }));
  const removeLine = (i) => setForm(p => ({ ...p, lines: p.lines.filter((_, idx) => idx !== i) }));
  const setLine = (i, k, v) => setForm(p => ({ ...p, lines: p.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));

  const totalDebit  = form.lines.reduce((s, l) => s + parseFloat(l.debit  || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + parseFloat(l.credit || 0), 0);
  const balanced    = Math.abs(totalDebit - totalCredit) < 0.01;

  const openCreate = () => {
    setForm({ date: new Date().toISOString().slice(0, 10), description: '', reference: '', status: 'draft', lines: [{ ...BLANK_LINE }, { ...BLANK_LINE }] });
    setModal(true);
  };

  const openView = async (id) => {
    try { const { data } = await api.get(`/accounting/journal/${id}`); setViewModal(data); }
    catch { alert('Could not load entry'); }
  };

  const handleSave = async () => {
    if (!form.description.trim()) return alert('Description is required');
    const validLines = form.lines.filter(l => l.account_id && (parseFloat(l.debit || 0) > 0 || parseFloat(l.credit || 0) > 0));
    if (validLines.length < 2) return alert('Need at least 2 lines with amounts');
    if (!balanced) return alert(`Entry is not balanced.\nDebit: ${fmtMoney(totalDebit)}\nCredit: ${fmtMoney(totalCredit)}`);
    setSaving(true);
    try {
      await api.post('/accounting/journal', { ...form, lines: validLines });
      setModal(false); load();
    } catch (err) { alert(err.response?.data?.error || 'Error creating entry'); }
    finally { setSaving(false); }
  };

  const handlePost = async (id) => {
    if (!window.confirm('Post this journal entry? Posted entries cannot be edited.')) return;
    try { await api.put(`/accounting/journal/${id}`, { status: 'posted' }); load(); if (viewModal?.id === id) { const { data } = await api.get(`/accounting/journal/${id}`); setViewModal(data); } }
    catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this journal entry?')) return;
    try { await api.delete(`/accounting/journal/${id}`); load(); if (viewModal?.id === id) setViewModal(null); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <select className="form-control" style={{ width: 150 }} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="posted">Posted</option>
          <option value="reversed">Reversed</option>
        </select>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={openCreate}>+ New Entry</button>
      </div>

      {loading ? <div className="loading">Loading…</div> : (
        <table>
          <thead><tr><th>Entry #</th><th>Date</th><th>Description</th><th>Reference</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={6} className="empty">No journal entries</td></tr>
              : items.map(item => (
              <tr key={item.id}>
                <td className="font-mono"><strong>{item.entry_number}</strong></td>
                <td>{item.date ? item.date.slice(0, 10) : '—'}</td>
                <td>{item.description}</td>
                <td className="text-muted">{item.reference || '—'}</td>
                <td><span className={`badge badge-${item.status}`}>{item.status}</span></td>
                <td>
                  <div className="actions">
                    <button className="btn-icon" title="View" onClick={() => openView(item.id)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                    </button>
                    {item.status === 'draft' && (
                      <>
                        <button className="btn-icon success" title="Post Entry" onClick={() => handlePost(item.id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                        </button>
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <span>Page {page} of {totalPages}</span>
          <div className="pagination-btns">
            <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
            <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-xl">
            <div className="modal-header">
              <h2>New Journal Entry</h2>
              <button className="modal-close" onClick={() => setModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" className="form-control" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Reference</label>
                  <input className="form-control" value={form.reference} onChange={e => setForm(p => ({ ...p, reference: e.target.value }))} placeholder="Invoice #, PO #, etc." />
                </div>
                <div className="form-group full">
                  <label>Description *</label>
                  <input className="form-control" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Journal entry description" />
                </div>
              </div>

              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Journal Lines</div>
              <div className="items-table">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '35%' }}>Account</th>
                      <th style={{ width: '25%' }}>Description</th>
                      <th style={{ width: '15%' }} className="text-right">Debit</th>
                      <th style={{ width: '15%' }} className="text-right">Credit</th>
                      <th style={{ width: '10%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((line, i) => (
                      <tr key={i}>
                        <td>
                          <select className="form-control" value={line.account_id} onChange={e => setLine(i, 'account_id', e.target.value)}>
                            <option value="">Select account…</option>
                            {ACCOUNT_TYPES.map(t => (
                              <optgroup key={t} label={t.charAt(0).toUpperCase() + t.slice(1)}>
                                {accounts.filter(a => a.type === t && a.is_active).map(a => (
                                  <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        </td>
                        <td><input className="form-control" value={line.description} onChange={e => setLine(i, 'description', e.target.value)} placeholder="Optional" /></td>
                        <td><input type="number" className="form-control" value={line.debit} onChange={e => { setLine(i, 'debit', e.target.value); if (e.target.value) setLine(i, 'credit', ''); }} step="0.01" min="0" placeholder="0.00" /></td>
                        <td><input type="number" className="form-control" value={line.credit} onChange={e => { setLine(i, 'credit', e.target.value); if (e.target.value) setLine(i, 'debit', ''); }} step="0.01" min="0" placeholder="0.00" /></td>
                        <td>
                          {form.lines.length > 2 && (
                            <button className="btn-icon danger" onClick={() => removeLine(i)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="items-total" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <button className="btn btn-ghost btn-sm" onClick={addLine}>+ Add Line</button>
                  <div style={{ display: 'flex', gap: 24 }}>
                    <span>Debit: <strong>{fmtMoney(totalDebit)}</strong></span>
                    <span>Credit: <strong>{fmtMoney(totalCredit)}</strong></span>
                    <span style={{ color: balanced ? '#15803d' : '#dc2626', fontWeight: 600 }}>
                      {balanced ? '✓ Balanced' : `Diff: ${fmtMoney(Math.abs(totalDebit - totalCredit))}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-ghost" onClick={() => { setForm(p => ({ ...p, status: 'draft' })); handleSave(); }}>Save as Draft</button>
              <button className="btn btn-primary" onClick={() => { setForm(p => ({ ...p, status: 'posted' })); setTimeout(handleSave, 0); }} disabled={saving || !balanced}>
                {saving ? 'Saving…' : 'Post Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewModal(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>{viewModal.entry_number}</h2>
              <button className="modal-close" onClick={() => setViewModal(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ marginBottom: 12 }}>
                <div className="form-group"><label>Entry #</label><div className="font-mono">{viewModal.entry_number}</div></div>
                <div className="form-group"><label>Date</label><div>{viewModal.date ? viewModal.date.slice(0, 10) : '—'}</div></div>
                <div className="form-group"><label>Status</label><div><span className={`badge badge-${viewModal.status}`}>{viewModal.status}</span></div></div>
                <div className="form-group"><label>Reference</label><div>{viewModal.reference || '—'}</div></div>
                <div className="form-group full"><label>Description</label><div>{viewModal.description}</div></div>
              </div>
              <div className="items-table">
                <table>
                  <thead><tr><th>Account</th><th>Description</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
                  <tbody>
                    {(viewModal.lines || []).map((l, i) => (
                      <tr key={i}>
                        <td><span className="font-mono text-muted">{l.account_code}</span> {l.account_name}</td>
                        <td className="text-muted">{l.description || '—'}</td>
                        <td className="text-right">{parseFloat(l.debit) > 0 ? fmtMoney(l.debit) : '—'}</td>
                        <td className="text-right">{parseFloat(l.credit) > 0 ? fmtMoney(l.credit) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="items-total" style={{ display: 'flex', justifyContent: 'flex-end', gap: 32 }}>
                  <span>Debit: <strong>{fmtMoney(viewModal.total_debit)}</strong></span>
                  <span>Credit: <strong>{fmtMoney(viewModal.total_credit)}</strong></span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {viewModal.status === 'draft' && (
                <button className="btn btn-success btn-sm" onClick={() => handlePost(viewModal.id)}>Post Entry</button>
              )}
              <button className="btn btn-ghost" onClick={() => setViewModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Ledger ───────────────────────────────────────────────── */
function Ledger() {
  const [accounts, setAccounts] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [from, setFrom]   = useState('');
  const [to, setTo]       = useState('');
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/accounting/accounts').then(r => setAccounts(r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    if (!selectedId) return alert('Select an account');
    setLoading(true);
    try {
      const { data } = await api.get(`/accounting/ledger/${selectedId}?from=${from}&to=${to}`);
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  return (
    <div className="tab-content">
      <div className="filter-row">
        <div className="form-group" style={{ minWidth: 260 }}>
          <label>Account</label>
          <select className="form-control" value={selectedId} onChange={e => setSelectedId(e.target.value)}>
            <option value="">Select account…</option>
            {ACCOUNT_TYPES.map(t => (
              <optgroup key={t} label={t.charAt(0).toUpperCase() + t.slice(1)}>
                {accounts.filter(a => a.type === t && a.is_active).map(a => (
                  <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>From</label>
          <input type="date" className="form-control" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label>To</label>
          <input type="date" className="form-control" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-end' }} onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'View Ledger'}
        </button>
      </div>

      {data && (
        <>
          <div style={{ marginBottom: 12, display: 'flex', gap: 24, alignItems: 'center' }}>
            <div>
              <strong>{data.account.code} — {data.account.name}</strong>
              <span className={`badge ${TYPE_COLORS[data.account.type]}`} style={{ marginLeft: 8 }}>{data.account.type}</span>
            </div>
            <div className="text-muted" style={{ marginLeft: 'auto', fontSize: 13 }}>
              Closing Balance: <strong>${fmtMoney(data.closing_balance)}</strong>
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Date</th><th>Entry #</th><th>Description</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Balance</th></tr>
              </thead>
              <tbody>
                {data.lines.length === 0 ? (
                  <tr><td colSpan={6} className="empty">No posted transactions found</td></tr>
                ) : data.lines.map((l, i) => (
                  <tr key={i}>
                    <td>{l.date ? l.date.slice(0, 10) : '—'}</td>
                    <td className="font-mono text-muted">{l.entry_number}</td>
                    <td>{l.description || l.entry_desc}</td>
                    <td className="text-right">{parseFloat(l.debit) > 0 ? <span className="qty-in">{fmtMoney(l.debit)}</span> : '—'}</td>
                    <td className="text-right">{parseFloat(l.credit) > 0 ? <span className="qty-out">{fmtMoney(l.credit)}</span> : '—'}</td>
                    <td className="text-right"><strong>{fmtMoney(l.running_balance)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!data && !loading && <div className="empty">Select an account and click "View Ledger"</div>}
    </div>
  );
}

/* ─── Trial Balance ────────────────────────────────────────── */
function TrialBalance() {
  const [data, setData]     = useState(null);
  const [asOf, setAsOf]     = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/accounting/trial-balance?as_of=${asOf}`);
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="tab-content">
      <div className="filter-row" style={{ marginBottom: 16 }}>
        <div className="form-group">
          <label>As of Date</label>
          <input type="date" className="form-control" value={asOf} onChange={e => setAsOf(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-end' }} onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Generate'}
        </button>
        <button className="btn btn-ghost btn-sm no-print" style={{ alignSelf: 'flex-end' }} onClick={() => window.print()}>Print</button>
      </div>

      {data && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
            <strong>Trial Balance — As of {asOf}</strong>
            <span className={`badge ${data.balanced ? 'badge-active' : 'badge-overdue'}`}>
              {data.balanced ? '✓ Balanced' : '✗ Not Balanced'}
            </span>
          </div>

          {ACCOUNT_TYPES.map(t => {
            const typeAccounts = (data.accounts || []).filter(a => a.type === t);
            if (typeAccounts.length === 0) return null;
            return (
              <div key={t} className="report-section">
                <h4 style={{ textTransform: 'capitalize' }}>{t}</h4>
                <table>
                  <thead><tr><th>Code</th><th>Account Name</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
                  <tbody>
                    {typeAccounts.map((a, i) => (
                      <tr key={i}>
                        <td className="font-mono text-muted">{a.code}</td>
                        <td>{a.name}</td>
                        <td className="text-right">{a.balance_debit > 0 ? fmtMoney(a.balance_debit) : '—'}</td>
                        <td className="text-right">{a.balance_credit > 0 ? fmtMoney(a.balance_credit) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          <table style={{ marginTop: 8 }}>
            <tfoot>
              <tr style={{ background: '#f8fafc' }}>
                <td colSpan={2}><strong>TOTALS</strong></td>
                <td className="text-right"><strong>{fmtMoney(data.total_debit)}</strong></td>
                <td className="text-right"><strong>{fmtMoney(data.total_credit)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </>
      )}
      {!data && !loading && <div className="empty">Click Generate to view trial balance</div>}
    </div>
  );
}
